import { kpiApi } from "@/api/kpiApi";
import Loading from "@/components/feedback/Loading";
import {
    Box,
    Button,
    FormControl,
    FormHelperText,
    FormLabel,
    MenuItem,
    TextField as MuiTextField,
    Stack,
} from "@mui/material";
import { Field, Form, FormikProvider, useFormik } from "formik";
import { Autocomplete, Select } from "formik-mui";
import * as _ from "lodash";
import { useCallback, useEffect } from "react";
import * as yup from "yup";

export function CreateNewRelationForm({ kpiId, existRelations = [], onSubmit, afterSubmit, onCancel }) {
    const [refreshKpis, kpis] = kpiApi.endpoints.searchKpi.useLazyQuery();
    const options = kpiApi.endpoints.getKpiRelationOptions.useQuery({ kpiId });
    const relationTypes = options.data?.data?.relation_types ?? [];

    const debounceRefreshKpis = useCallback(
        _.debounce((search) => refreshKpis({ search: search, page: { size: 200 } }), 500),
        [refreshKpis]
    );
    const getOptionDisabled = (option) => {
        if (option.id == kpiId) return true;

        return (
            -1 !==
            existRelations.findIndex(
                (relation) => relation.type.name == values.relation_type && relation.target.id == option.id
            )
        );
    };
    const [createRelation] = kpiApi.endpoints.createKpiRelation.useMutation();

    const formik = useFormik({
        initialValues: {
            relation_type: "",
            target_kpi: null,
        },
        validationSchema: yup.object().shape({
            relation_type: yup.string().required(),
            target_kpi: yup.object().required(),
        }),
        onSubmit: async (values, { setSubmitting, resetForm }) => {
            setSubmitting(true);
            await createRelation({
                kpiId: kpiId,
                data: {
                    relation_type: values.relation_type,
                    target_kpi_id: values.target_kpi.id,
                },
            }).unwrap();

            setSubmitting(false);
            resetForm();

            if (afterSubmit instanceof Function) {
                afterSubmit();
            }
        },
        onReset: () => {
            if (onCancel instanceof Function) {
                onCancel();
            }
        },
    });
    const { touched, errors, values } = formik;

    useEffect(() => {
        refreshKpis({ search: "", page: { size: 200 } });
    }, []);

    if (options.isLoading) {
        return <Loading />;
    }

    return (
        <FormikProvider value={formik}>
            <Form>
                <Stack spacing={2}>
                    <FormControl fullWidth required>
                        <FormLabel>ประเภทความสัมพันธ์</FormLabel>
                        <Field component={Select} name="relation_type">
                            {relationTypes?.map((relationType) => (
                                <MenuItem key={relationType.id} value={relationType.name}>
                                    {relationType.name_display}
                                </MenuItem>
                            ))}
                        </Field>
                        <FormHelperText name="relation_type" touched={touched} errors={errors} />
                    </FormControl>

                    <FormControl fullWidth required>
                        <FormLabel>ตัวชี้วัด</FormLabel>
                        <Field
                            component={Autocomplete}
                            name="target_kpi"
                            options={kpis.data?.data ?? []}
                            loading={kpis.isFetching}
                            isOptionEqualToValue={(option, value) => option.id === value?.id}
                            getOptionLabel={(option) => option.title}
                            getOptionDisabled={getOptionDisabled} // style={{ width: 300 }}
                            renderInput={(params) => <MuiTextField {...params} label="ค้นหา" variant="outlined" />}
                            renderOption={(props, option) => (
                                <MenuItem {...props} key={option.id}>
                                    {option.title} [{option.kpi_code}] (#
                                    {option.id})
                                </MenuItem>
                            )}
                            onInputChange={(event, value) => debounceRefreshKpis(value)}
                        />
                        <FormHelperText name="target_kpi" touched={touched} errors={errors} />
                    </FormControl>

                    <Box sx={{ display: "flex", gap: 1 }}>
                        <Button type="submit" variant="contained">
                            add
                        </Button>
                        <Button type="reset">cancel</Button>
                    </Box>
                </Stack>
            </Form>
        </FormikProvider>
    );
}
